import addDays from 'date-fns/add_days';
import subDays from 'date-fns/sub_days';
import subMonths from 'date-fns/sub_months';
import isEmpty from 'lodash/isEmpty';
import tail from 'lodash/tail';
import get from 'lodash/get';
import head from 'lodash/head';

import env from 'Env';
import GeneralUtils from 'Helpers/GeneralUtils';
import { currencyCode } from 'Helpers/Geo/Country';
import { get as apiGet } from 'Api';
import store, { history } from 'Redux/store';
import { encodeSlug } from 'Helpers/Slug';
import { getIndex, getClient } from 'Api/Algolia/algolia-setup';

// If set to false, alt rank split test will test the base alt rank, otherwise regional ranks will be used.
// const USE_REGIONS = false;

const today = Math.round((new Date()).getTime() / 1000);
const plusTwoDays = Math.round(addDays(new Date(), 2).getTime() / 1000);
const minusSevenDays = Math.round(subDays(new Date(), 7).getTime() / 1000);
const minusOneMonth = Math.round(subMonths(new Date(), 1).getTime() / 1000);
const minusTwoMonths = Math.round(subMonths(new Date(), 2).getTime() / 1000);
const minusThreeMonths = Math.round(subMonths(new Date(), 3).getTime() / 1000);
const globalAttributesToRetrieve = [
  'age_ratings',
  'available_valid_from',
  'available_valid_until',
  'best_ever',
  'bundle_covers',
  'collections',
  'cover',
  'cover_alt_text',
  'cover_type',
  'discount_percent',
  'discount_slug',
  'display_type',
  'dlc_total',
  'drm',
  'flash_sale',
  'free_to_play',
  'fullPrice',
  'game_total',
  'giveaway',
  'hideDLC',
  'hide_discount',
  'hide_price',
  'is_srp_bundle',
  'metacritic_score',
  'mystery',
  'mystery_homepage',
  'name',
  'no_release_date',
  'on_sale',
  'operating_systems',
  'pay_what_you_want',
  'pnm_saving',
  'portrait_cover',
  'position',
  'preorder_playable_from',
  'presale',
  'price',
  'product_id',
  'rating_score',
  'rating_score',
  'release_date',
  'reveal_template',
  'screenshots',
  'slug',
  'sponsored_discount',
  'star_deal',
  'tiered',
  'total_ratings',
  'type',
  'video',
  'video_clip_files',
  'video_clip_poster',
  'lowest_in_30_days',
];

// const countriesEU = [
//   'EU', 'AX', 'AL', 'AD', 'AT', 'BY', 'BE',
//   'BA', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FO',
//   'FI', 'FR', 'DE', 'GI', 'GR', 'GG', 'VA',
//   'HU', 'IS', 'IE', 'IM', 'IT', 'JE', 'LV',
//   'LI', 'LT', 'LU', 'MK', 'MT', 'MD', 'MC',
//   'ME', 'NL', 'NO', 'PL', 'PT', 'RO', 'RU',
//   'SM', 'RS', 'SK', 'SI', 'ES', 'SJ', 'SE',
//   'CH', 'TR', 'UA', 'GB', 'XK',
// ];

// const countriesUS = [
//   'US', // United States
//   'CA', // Canada
// ];

/**
 * Which region specific index should the user have?
 * @param  {String} userCountry
 * @return {String}
 */
// const regionSpecificIndex = (userCountry) => {
//   if (!USE_REGIONS) {
//     return 'alt_rank';
//   }

//   if (!userCountry) return 'alt_rank_row';

//   let rankName = 'alt_rank_row';

//   if (countriesUS.includes(userCountry)) {
//     rankName = 'alt_rank_us';
//   } else if (countriesEU.includes(userCountry)) {
//     rankName = 'alt_rank_eu';
//   }

//   return rankName;
// };

// const isAltRank = () => IsChallenger(USE_REGIONS ? '556RegionalTopSellersRank' : 'AltTopSellersRank');

export const indexAlgoliaName = env.algolia.indexName;

/**
 * Check split test and return standard top sellers or alt rank index name
 * @param  {Boolean} unlimited     Use unlimited version of algolia top sellers
 * @return {String}
 */
// export const topSellerIndex = unlimited => (isAltRank() ? `${indexAlgoliaName}_${regionSpecificIndex(countryCode)}` : `${indexAlgoliaName}${unlimited ? '_unlimited' : ''}`);
export const topSellerIndex = unlimited => (`${indexAlgoliaName}${unlimited ? '_unlimited' : ''}`);
// const apiAlgoliaSplitTest = path => `${path}${path.includes('?') ? '&' : '?'}altRank=${isAltRank()}`;
const apiAlgoliaSplitTest = path => path;

const createSlugsToExcludeFilter = (slugs) => {
  const headSlugString = head(slugs);
  const tailingSlugs = tail(slugs);
  let tailingSlugsString = '';

  if (!isEmpty(tailingSlugs)) {
    tailingSlugsString = tailingSlugs.map(slug => `AND NOT slug:"${slug}"`).join(' ');
  }

  return `(NOT slug:"${headSlugString}" ${tailingSlugsString})`;
};

const createNamesToExcludeFilter = (names) => {
  const headNameString = head(names);
  const tailingNames = tail(names);
  let tailingNamesString = '';

  if (!isEmpty(tailingNames)) {
    tailingNamesString = tailingNames.map(name => `AND NOT name:"${name}"`).join(' ');
  }

  return `(NOT name:"${headNameString}" ${tailingNamesString})`;
};

// factFilters helper
// ["category:Book", "author:John Doe"] translates as category:Book AND author:"John Doe".
// [["category:Book", "category:Movie"]] translates as category:Book OR category:Movie.
// [["category:Book", "category:Movie"], "author:John Doe"] translates as (category:Book OR category:Movie) AND author:"John Doe".

export async function fetchLatestDealsSection(hitsPerPage) {
  const indexLatestDeals = await getIndex((`${indexAlgoliaName}_latest_deals`));
  return indexLatestDeals.search('', {
    filters: `discount_percent > 0 AND available_valid_from > ${minusSevenDays} AND release_date < ${minusOneMonth} AND position > 11`,
    hitsPerPage: hitsPerPage || 4,
    analyticsTags: ['frontend.fetchLatestDealsSection'],
  }).then(latestDealsSection => latestDealsSection);
}

export async function fetchTrendingDealsCarousel(hitsPerPage = 12, slugsToExclude) {
  const index = await getIndex(`${indexAlgoliaName}_best_price_rank`);
  return index.search('', {
    filters: `${createSlugsToExcludeFilter(slugsToExclude)} AND mystery_homepage != 1 AND NOT display_type:bundle AND NOT display_type:book-bundle AND discount_percent > 0`,
    hitsPerPage: hitsPerPage || 12,
    analyticsTags: ['frontend.fetchTrendingDealsCarousel'],
  }).then(hits => hits.hits);
}

export async function fetchExtraCardPanelHits(hitsPerPage, slugsToExclude) {
  const index = await getIndex(`${indexAlgoliaName}_best_price_rank`);

  return index.search('', {
    filters: `${createSlugsToExcludeFilter(slugsToExclude)}`,
    hitsPerPage: hitsPerPage || 5,
    analyticsTags: ['frontend.fetchLatestDealsSection'],
  }).then(hits => hits.hits);
}

export async function fetchLatestDealsCarousel(hitsToDisplay = 12, slugsToExclude) {
  const indexLatestDeals = await getIndex(`${indexAlgoliaName}_latest_deals`);
  return indexLatestDeals.search('', {
    filters: `${createSlugsToExcludeFilter(slugsToExclude)} AND discount_percent > 0 AND available_valid_from > ${minusSevenDays} AND release_date < ${minusOneMonth} AND mystery_homepage != 1 AND NOT display_type:bundle AND NOT display_type:book-bundle `,
    hitsPerPage: hitsToDisplay,
    analyticsTags: ['frontend.fetchLatestDealsCarousel'],
  }).then(latestDealsSection => latestDealsSection.hits);
}


export async function fetchCheapUpsellGames(priceFilter, hitsPerPage, slugsToExclude) {
  const index = await getIndex(topSellerIndex());

  return index.search('', {
    hitsPerPage: hitsPerPage || 12,
    filters: `${createSlugsToExcludeFilter(slugsToExclude)} AND ${priceFilter} AND display_type:game AND mystery != 1 AND giveaway != 1`,
    analyticsTags: ['frontend.fetchCheapUpsellGames'],
  }).then(results => results.hits);
}

export async function fetchBundleCarousel(hitsPerPage, slugsToExclude) {
  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);
  const indexBundles = await getIndex(topSellerIndex());
  return indexBundles.search('', {
    // eslint-disable-next-line max-len
    facetFilters: 'display_type:-book-bundle',
    filters: `${createSlugsToExcludeFilter(slugsToExclude)} AND display_type:bundle AND NOT display_type:software-bundle AND mystery_homepage != 1 AND NOT drm:switch AND NOT drm:threeds AND NOT display_type:elearning-bundle AND NOT display_type:audio-bundle`,
    hitsPerPage: hitsPerPage || 12,
    attributesToRetrieve,
    analyticsTags: ['frontend.fetchBundleCarousel'],
  }).then(bundleSection => bundleSection.hits);
}

const createTypeFilters = (filters) => { // eslint-disable-line no-unused-vars
  const filterObj = {
    games: 'display_type:game',
    bundles: 'display_type:software-bundle OR display_type:bundle OR display_type:book-bundle OR display_type:elearning-bundle OR display_type:elearning OR display_type:comic-bundle OR display_type:audio-bundle',
    dlc: 'display_type:dlc',
  };

  const filtersArray = Object.keys(filters).filter(key => filters[key]);
  let headFilterString = '';

  const headFilter = filterObj[filtersArray[0]];

  if (headFilter) {
    headFilterString = `AND ${headFilter}`;
  }

  let tailingFiltersSting = '';

  const tailingFilters = tail(filtersArray);

  if (!isEmpty(tailingFilters)) {
    tailingFiltersSting = tailingFilters.map(filter => `OR ${filterObj[filter]}`).join(' ');
  }

  return `${headFilterString} ${tailingFiltersSting}`;
};

export async function fetchEndingSoonSection() {
  const indexEndingSoon = await getIndex(topSellerIndex());
  return indexEndingSoon.search('', {
    filters: `discount_percent > 0 AND available_valid_until < ${plusTwoDays}`,
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchEndingSoonSection'],
  });
}

// filters: `release_date > ${minusTwoMonths} AND release_date < ${today} AND (display_type:game OR display_type:dlc)`,

export async function fetchNewReleasesSection() {
  const indexNewReleases = await getIndex(topSellerIndex());
  return indexNewReleases.search('', {
    filters: `release_date > ${minusTwoMonths} AND release_date < ${today} AND (display_type:game OR display_type:dlc)`,
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchNewReleasesSection'],
  }).then(hits => hits.hits);
}

export async function fetchMetacriticNewReleases() {
  const indexNewReleases = await getIndex(`${indexAlgoliaName}_metacritic`);
  return indexNewReleases.search('', {
    filters: `release_date > ${minusThreeMonths} AND release_date < ${today} AND metacritic_score > 0 AND presale != 1`,
    hitsPerPage: 30,
    analyticsTags: ['frontend.fetchMetacriticNewReleases'],
  }).then(hits => hits.hits);
}

// filters: `release_date > ${minusTwoMonths} AND release_date < ${today} AND metacritic_score > 0`,

export async function fetchMostWantedNewReleases() {
  const indexMostWanted = await getIndex(`${indexAlgoliaName}_most_wanted`);
  return indexMostWanted.search('', {
    filters: `release_date > ${minusThreeMonths} AND release_date < ${today} AND type:game OR type:dlc`,
    hitsPerPage: 10,
    analyticsTags: ['frontend.fetchMostWantedNewReleases'],
  }).then(hits => hits.hits);
}

export async function fetchVrSection() {
  const indexNewReleases = await getIndex(topSellerIndex());
  return indexNewReleases.search('', {
    facetFilters: [['features:VR Support', 'slug:platinum-vr-collection-build-your-own-bundle']],
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchVrSection'],
  });
}

export async function fetchSteamDeckGamesSection() {
  const indexNewReleases = await getIndex(topSellerIndex());
  return indexNewReleases.search('', {
    facetFilters: [['steam_deck_support:verified', 'steam_deck_support:playable']],
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchSteamDeckGamesSection'],
  });
}

export async function fetchBundles(hitsPerPage = 100) {
  const indexBundles = await getIndex(topSellerIndex());
  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);
  return indexBundles.search('', {
    filters: 'display_type:bundle AND NOT display_type:software-bundle',
    hitsPerPage,
    attributesToRetrieve,
    analyticsTags: ['frontend.fetchBundles'],
  }).then(bundles => bundles);
}

export async function fetchSoftwareBundleSection() {
  const indexBundles = await getIndex(topSellerIndex());
  return indexBundles.search('', {
    filters: 'display_type:software-bundle',
    hitsPerPage: 4,
    analyticsTags: ['frontend.fetchSoftwareBundleSection'],
  }).then(softwareBundleSection => softwareBundleSection);
}

export async function fetchBundleFranchiseSection() {
  const indexBundles = await getIndex(topSellerIndex());
  return indexBundles.search('', {
    // eslint-disable-next-line max-len
    facetFilters: 'franchises:Franchise Bundles',
    filters: 'display_type:bundle',
    hitsPerPage: 4,
    analyticsTags: ['frontend.fetchBundleFranchiseSection'],
  }).then(bundleFranchiseSection => bundleFranchiseSection);
}

export async function fetchComingSoonSection() {
  const indexBundles = await getIndex(topSellerIndex());
  return indexBundles.search('', {
    filters: `release_date > ${today} AND position > 23 AND NOT type:pick-and-mix`,
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchComingSoonSection'],
  }).then(comingSoonSection => comingSoonSection.hits);
}

export async function fetchFlashDeals(currentPage) {
  const indexFlashDeals = await getIndex(topSellerIndex());
  return indexFlashDeals.search('', {
    page: currentPage,
    hitsPerPage: 36,
    filters: 'flash_sale = 1 AND discount_percent > 0',
    analyticsTags: ['frontend.fetchFlashDeals'],
  }).then(hits => hits);
}

export async function fetchFreeComicFlashDeals() {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    // filters: 'flash_sale = 1 AND giveaway = 1 AND display_type:comic OR display_type:graphic-novel',
    filters: 'giveaway = 1 AND display_type:comic OR display_type:graphic-novel',
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchFreeComicFlashDeals'],
  }).then(topSellers => topSellers.hits);
}

export async function fetchFlashDealsSection() {
  const { data } = await apiGet([], apiAlgoliaSplitTest('/algolia/flash-sale'));
  return data;
}

export async function fetchAllTopSellersSection(hitsPerPage) {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    facetFilters: ['publishers:-ea', 'publishers:-bethesda-softworks'],
    filters: 'mystery_homepage != 1 AND NOT drm:switch AND NOT drm:threeds AND NOT display_type:software-bundle AND NOT display_type:bundle AND NOT display_type:elearning-bundle AND NOT display_type:elearning AND NOT display_type:book-bundle AND NOT display_type:audio-bundle',
    hitsPerPage: hitsPerPage || 15,
    analyticsTags: ['frontend.fetchAllTopSellersSection'],
  }).then(topSellers => topSellers.hits);
}

export async function fetchTopSellersSection(hitsPerPage, seperateBundles) {
  const reduxStore = store.getState();
  const flashSale = get(reduxStore, 'flashSale');
  let algoliaFilters = `mystery_homepage != 1 AND NOT drm:switch AND NOT drm:threeds AND NOT display_type:software-bundle ${seperateBundles ? 'AND NOT display_type:bundle' : ''} AND NOT display_type:elearning-bundle AND NOT display_type:elearning AND NOT display_type:audio-bundle AND NOT display_type:audio`;
  // Only exclude flash sale products if promotion is running
  if (!isEmpty(flashSale)) {
    algoliaFilters = `flash_sale != 1 AND mystery_homepage != 1 AND NOT drm:switch AND NOT drm:threeds AND NOT display_type:software-bundle ${seperateBundles ? 'AND NOT display_type:bundle' : ''} AND NOT display_type:elearning-bundle AND NOT display_type:elearning AND NOT display_type:audio-bundle AND NOT display_type:audio`;
  }

  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    facetFilters: [['display_type:-book-bundle']],
    filters: algoliaFilters,
    hitsPerPage: hitsPerPage || 11,
    analyticsTags: ['frontend.fetchTopSellersSection'],
  }).then(topSellers => topSellers.hits);
}

export async function fetchTopSellersSectionWithMystery(hitsPerPage, seperateBundles) {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    facetFilters: 'display_type:-book-bundle',
    filters: `NOT drm:switch AND NOT drm:threeds ${seperateBundles ? 'AND NOT display_type:bundle' : ''}`,
    hitsPerPage: hitsPerPage || 11,
    analyticsTags: ['frontend.fetchTopSellersSectionWithMystery'],
  }).then(topSellers => topSellers.hits);
}

export async function fetchMysterySection(hitsPerPage) {
  const indexName = topSellerIndex();
  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`, 'bundle_tiers']);
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    facetFilters: 'display_type:bundle',
    filters: 'mystery = 1 AND mystery_upsell != 1',
    attributesToRetrieve,
    hitsPerPage: hitsPerPage || 4,
    analyticsTags: ['frontend.fetchMysterySection'],
  }).then(topSellers => topSellers.hits);
}

export async function fetchTopSellersGamesSection(hitsPerPage) {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    hitsPerPage: hitsPerPage || 15,
    filters: 'flash_sale != 1 AND mystery_homepage != 1 AND type:game OR type:dlc',
    analyticsTags: ['frontend.fetchTopSellersGamesSection'],
  }).then(topSellersGames => topSellersGames.hits);
}

export async function fetchMacGamesSection(hitsPerPage) {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    hitsPerPage: hitsPerPage || 15,
    filters: 'operating_systems:mac AND flash_sale != 1 AND mystery_homepage != 1 AND display_type:game OR display_type:dlc',
    analyticsTags: ['frontend.fetchMacGamesSection'],
  }).then(topSellersGames => topSellersGames.hits);
}

export async function fetchLinuxGamesSection(hitsPerPage) {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    hitsPerPage: hitsPerPage || 15,
    filters: 'operating_systems:linux AND flash_sale != 1 AND mystery_homepage != 1 AND display_type:game OR display_type:dlc',
    analyticsTags: ['frontend.fetchLinuxGamesSection'],
  }).then(topSellersGames => topSellersGames.hits);
}

export async function fetchPublisher(publisher, indexName, page = 0) {
  const index = await getIndex(indexName ? `${indexAlgoliaName}_${indexName}` : topSellerIndex());
  return index.search('', {
    facetFilters: `publishers:${publisher}`,
    hitsPerPage: 100,
    page,
    analyticsTags: ['frontend.fetchPublisher'],
  });
}

export async function fetchFreeToPlaySection(hitsPerPage) {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  return indexTopSellers.search('', {
    hitsPerPage: hitsPerPage || 15,
    filters: 'free_to_play = 1 AND (type:game OR type:dlc)',
    analyticsTags: ['frontend.fetchFreeToPlaySection'],
  }).then(topSellersGames => topSellersGames.hits);
}

export async function fetchSingleFlashDealSection() {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);
  return indexTopSellers.search('', {
    hitsPerPage: 1,
    filters: 'flash_sale = 1 AND discount_percent > 0',
    attributesToRetrieve,
    analyticsTags: ['frontend.fetchSingleFlashDealSection'],
  }).then(singleFlashDeal => singleFlashDeal.hits);
}

export async function fetchTopSellers(type, count) {
  const { locale } = window;
  const { data } = await apiGet([], apiAlgoliaSplitTest(`/algolia/top-sellers${type ? `/${type}` : ''}?lang=${locale}${count ? `&count=${count}` : ''}`));
  return data;
}

export async function fetchTopSellersExclude(hitsPerPage, slugsToExclude) {
  const indexName = topSellerIndex();
  const indexTopSellers = await getIndex(indexName);
  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);
  return indexTopSellers.search('', {
    hitsPerPage: hitsPerPage || 11,
    facets: ['display_type'],
    page: 0,
    attributesToRetrieve,
    filters: createSlugsToExcludeFilter(slugsToExclude),
    analyticsTags: ['frontend.fetchTopSellersExclude'],
  }).then(results => results.hits);
}

export async function fetchSearchHits(facet, value, dashValue, page) {
  const index = await getIndex(topSellerIndex());
  return index.search('', {
    hitsPerPage: 36,
    page,
    facetFilters: [[`${facet}:${value}`, `${facet}:${dashValue}`]],
    filters: 'NOT display_type:bundle',
    analyticsTags: ['frontend.fetchSearchHits'],
  }).then(searchHits => searchHits);
}

export async function query(searchString, hitsPerPage, slugsToExclude) {
  const index = await getIndex(topSellerIndex());
  return index.search(searchString || '', {
    filters: createSlugsToExcludeFilter(slugsToExclude),
    hitsPerPage: hitsPerPage || 3,
    analyticsTags: ['frontend.query'],
  }).then(searchHits => searchHits.hits);
}

export async function fetchStaticQuery({ facet, value, callPage, facets = [], type, index }) {
  const searchIndex = index ? `${indexAlgoliaName}_${index}` : topSellerIndex();
  const indexNewReleases = await getIndex(searchIndex);
  const attributesToRetrieve = globalAttributesToRetrieve.concat([facet, ...facets, 'sdesc', `sdescs.${window.locale}`, 'collectionsSlug', 'franchisesSlug', 'publishersSlug', 'categoriesSlug']);

  const searchQuery = {
    facetFilters: `${facet}Slug:${value}`,
    page: callPage,
    facets,
    filters: '',
    hitsPerPage: 36,
    attributesToRetrieve,
    analyticsTags: ['frontend.fetchStaticQuery', `frontend.fetchStaticQuery.${facet}`],
  };

  if (index === 'price_asc') searchQuery.filters += 'presale != 1';
  if (type) searchQuery.filters += `${searchQuery.filters ? ' AND ' : ''}type: ${type}`;

  return indexNewReleases.search('', searchQuery);
}

export async function fetchXboxGames(callPage, hitsPerPage, filterType, sort) {
  const index = await getIndex(sort ? `${indexAlgoliaName}_${sort}` : topSellerIndex());
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${window.locale}`]);

  let displayTypeFilter = 'NOT display_type:bundle';
  if (filterType === 'game') displayTypeFilter = 'display_type:game';
  if (filterType === 'dlc') displayTypeFilter = 'display_type:dlc';

  return index.search('', {
    facets: ['display_type'],
    filters: `drm:xbox AND NOT display_type:gift-card AND free_to_play != 1 AND ${displayTypeFilter}`,
    page: callPage,
    hitsPerPage: hitsPerPage || 36,
    attributesToRetrieve,
    analyticsTags: ['frontend.fetchXboxGames'],
  });
}

export async function fetchFacets(facet) {
  const indexTopSellers = await getIndex(indexAlgoliaName);
  return indexTopSellers.search('', {
    attributesToRetrieve: [facet],
    facets: [facet],
    sortFacetValuesBy: 'count',
    maxValuesPerFacet: 1000,
    facetingAfterDistinct: true,
    page: 0,
    analyticsTags: ['frontend.fetchFacets'],
  });
}

export async function fetchStaffPicks(hitsPerPage) {
  const indexTopSellers = await getIndex(topSellerIndex());
  return indexTopSellers.search('', {
    facetFilters: 'collections:Staff Picks',
    hitsPerPage: hitsPerPage || 6,
    analyticsTags: ['frontend.fetchStaffPicks'],
  });
}

// export async function fetchStaffPicksSection(hitsPerPage) {
//   const { locale } = window;
//   const indexTopSellers = await getIndex(topSellerIndex());
//   const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);
//   return indexTopSellers.search('', {
//     facetFilters: 'collections:Staff Picks',
//     hitsPerPage: hitsPerPage || 15,
//     attributesToRetrieve,
//     analyticsTags: ['frontend.fetchStaffPicksSection'],
//   });
// }

export async function fetchPublisherSection(publisher, slugsToExclude) {
  const indexTopSellers = await getIndex(topSellerIndex());
  return indexTopSellers.search('', {
    filters: `${createSlugsToExcludeFilter(slugsToExclude)} AND isEdition:false`,
    facetFilters: `publishers:${publisher}`,
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchPublisherSection'],
  });
}

export async function fetchCategorySection(category) {
  const indexTopSellers = await getIndex(topSellerIndex());
  return indexTopSellers.search('', {
    facetFilters: `categoriesSlug:${category}`,
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchCategorySection'],
  });
}

export async function fetchCollectionSection(collection) {
  const indexTopSellers = await getIndex(topSellerIndex());
  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);
  return indexTopSellers.search('', {
    // filters: 'type:pick-and-mix OR type:bundle',
    facetFilters: `collections:${collection}`,
    hitsPerPage: 15,
    attributesToRetrieve,
    analyticsTags: ['frontend.fetchCollectionSection'],
  });
}

export async function fetchBestOfSection() {
  const indexTopSellers = await getIndex(topSellerIndex());
  const yearStart = Date.parse('2020-01-01T00:00:00.000Z') / 1000;
  const yearEnd = Date.parse('2020-12-31T00:00:00.000Z') / 1000;
  return indexTopSellers.search('', {
    filters: `flash_sale != 1 AND release_date > ${yearStart} AND release_date < ${yearEnd} AND type:game OR type:dlc`,
    hitsPerPage: 15,
    analyticsTags: ['frontend.fetchBestOfSection'],
  });
}

// Used for Showcase
export async function fetchShowcase() {
  const indexTopSellers = await getIndex(topSellerIndex());
  return indexTopSellers.search('', {
    hitsPerPage: 25,
    filters: `release_date > ${minusThreeMonths} OR release_date > ${today} AND price.${currencyCode} >=20 AND display_type:game AND isEdition:false`,
    analyticsTags: ['frontend.fetchShowcase'],
  });
}

export async function fetchFranchises() {
  let franchises = [];
  const index = await getIndex(topSellerIndex());
  const { facets } = await index.search('', {
    attributesToRetrieve: ['franchises'],
    facets: ['franchises'],
    facetingAfterDistinct: true,
    maxValuesPerFacet: 1000,
    analyticsTags: ['frontend.fetchFranchises'],
  });

  if (!isEmpty(facets)) {
    franchises = Object.keys(facets.franchises).slice().sort();
  }

  return franchises;
}

export async function fetchProductBySlug(slug) {
  const index = await getIndex(topSellerIndex());
  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);
  return index.search('', {
    attributesToRetrieve,
    filters: `(slug:"${slug}")`,
    analyticsTags: ['frontend.fetchProductBySlug'],
  }).then(result => result.hits[0]);
}

export async function fetchProductById(id) {
  const index = await getIndex(topSellerIndex());
  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);
  return index.search('', {
    attributesToRetrieve,
    filters: `(product_id:"${id}")`,
    analyticsTags: ['frontend.fetchProductById'],
  }).then(result => result.hits[0]);
}

/**
 * concatenates a string from passed in product slugs that is suitable for
 * using as an algolia search filter paramater
 * @param  {Array.String} Slugs List product slugs
 * @return {String}      Product Slugs filter for algolia search
 */
const createSlugsFilterForAlgolia = (slugs) => {
  const headSlugString = slugs[0];
  const tailingSlugs = tail(slugs);
  let tailingSlugsString = '';

  if (tailingSlugs) {
    tailingSlugsString = tailingSlugs.map(slug => `OR slug:"${slug}"`).join(' ');
  }

  return `(slug:"${headSlugString}" ${tailingSlugsString})`;
};

export async function fetchBySlugs(slugLists, options = {}) {
  if (!slugLists || !Array.isArray(slugLists) || !slugLists.length) return false;

  let indexName = topSellerIndex();

  const { hitsPerPage, algoliaIndexName, componentSpecificTag } = options;

  if (algoliaIndexName) indexName = `${indexAlgoliaName}_${algoliaIndexName}`;

  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`, 'categories']);

  const client = await getClient();
  const queries = [];

  slugLists.forEach((slugs) => {
    queries.push({
      indexName,
      params: {
        filters: createSlugsFilterForAlgolia(slugs),
        hitsPerPage: hitsPerPage || 25,
        attributesToRetrieve,
        analyticsTags: [`frontend.fetchBySlugs${componentSpecificTag ? `.${componentSpecificTag}` : ''}`],
      },
    });
  });
  const results = await client.search(queries);

  if (!results || !results.results) {
    return slugLists.map(() => []);
  }

  return results.results.map((list, i) =>
    list.hits.sort((a, b) =>
      (slugLists[i].indexOf(a.slug) > slugLists[i].indexOf(b.slug) ? 1 : -1)));
}

export async function getBySlugsApi(slugs = [], sort, page = 1) {
  let result = { hits: [], count: 0 };
  if (isEmpty(slugs)) return result;

  let url = `/algolia/get-hits-by-slugs?slugs=${slugs.toString()}`;

  if (sort) {
    url += `&sort=${sort}`;
  }

  if (page) {
    url += `&page=${page}`;
  }

  try {
    const { response, data } = await apiGet([], url);
    if (response.ok) result = data;
  } catch (ex) { /* */ }

  return result;
}

/**
 * fetchEbookBundles fetches ebooks for the ebook landing page
 * @return {Array.Object}               results from algolia query
 */
export async function fetchEbookBundles() {
  const { data } = await apiGet([], apiAlgoliaSplitTest('/algolia/ebooks'));
  return data;
}

export async function fetchMixedBundles(indexName) {
  const { data: hits } = await apiGet([], apiAlgoliaSplitTest(`/algolia/bundles${indexName ? `/${indexName}` : ''}`));

  return hits || [];
}

export async function fetchGameBundles(slug) {
  const indexName = topSellerIndex();
  const indexBundles = await getIndex(indexName);
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['header']);

  let filters = 'display_type:bundle AND mystery_homepage != 1 AND mystery != 1';
  if (slug !== '') {
    filters = `display_type:bundle AND mystery_homepage != 1 AND mystery != 1 AND NOT slug:"${slug}"`;
  }

  return indexBundles.search('', {
    attributesToRetrieve,
    hitsPerPage: 4,
    filters,
    analyticsTags: ['frontend.fetchGameBundles'],
  }).then(results => results.hits);
}


/**
 * fetchEbookBundleCarousel gets top selling ebooks carousel on the home / bundle page
 * @return {Array.Object} ebook hits;
 */
export async function fetchEbookBundleCarousel() {
  const { data } = await apiGet([], apiAlgoliaSplitTest('/algolia/get-ebook-section'));
  return data;
}


export async function fetchMoreThanGames() {
  const { data } = await apiGet([], apiAlgoliaSplitTest('/algolia/get-more-than-games-section'));
  return data;
}

export async function fetchOnSale() {
  const { data } = await apiGet([], apiAlgoliaSplitTest('/algolia/onsaleresults'));
  return data;
}

export async function fetchHomeHits() {
  // Preloaded by server.js, so skip headers to match the preload request
  const { data } = await apiGet([], apiAlgoliaSplitTest('/algolia/home'), true);
  return data;
}

export async function fetchFeaturedCarousel() {
  const newUser = GeneralUtils.newUser();
  // Preloaded by server.js, so skip headers to match the preload request
  const { data } = await apiGet([], `/algolia/home-carousel/${window.locale}`, true);

  if (newUser) return data;

  // Remove giveaway slide for returning users (only for display_type game)
  return data.filter(slide => slide.type !== 'product' || (slide.type === 'product' && slide.hit && !(slide.hit.giveaway && slide.hit.display_type === 'game')));
}

export async function fetchPublisherCarousel() {
  const { data } = await apiGet([], '/algolia/publisher-carousel');
  return data;
}

export async function fetchFeaturedCarouselPreview(startDate) {
  const { response, data } = await apiGet([], `/algolia/home-carousel-preview/${window.locale}/startdate=${startDate}`);
  if (!response.ok) {
    history.push('/');
    // eslint-disable-next-line no-alert
    window.alert('Unathorized: Preview feature requires logging into admin account');
    return [];
  }
  return data;
}

export async function fetchGiftGuide() {
  const { data } = await apiGet([], apiAlgoliaSplitTest('/algolia/gift-guide'));
  return data;
}

export async function fetchNotFoundTopSellers(hitsPerPage, page = 0) {
  const indexName = topSellerIndex();
  const index = await getIndex(indexName);
  return index.search('', {
    hitsPerPage: hitsPerPage || 36,
    filters: 'type:game OR type:dlc OR display_type:bundle',
    page,
    analyticsTags: ['frontend.fetchNotFoundTopSellers'],
  }).then(results => results.hits);
}

export async function fetchProductFinderFacets(selectedFacets) {
  const index = await getIndex(topSellerIndex());
  let filters = '';

  Object.keys(selectedFacets).forEach((facet) => {
    if (!isEmpty(selectedFacets[facet])) {
      filters += `${filters.length ? ' AND ' : ''}${selectedFacets[facet].map(t => `${facet}Slug:${encodeSlug(t)}`).join(' AND ')}`;
    }
  });

  return index.search('', {
    hitsPerPage: 0,
    facets: ['genresSlug', 'playstylesSlug', 'themesSlug'],
    filters,
    analyticsTags: ['frontend.fetchProductFinderFacets'],
  }).then(hits => hits);
}

/**
 * Build an Algolia `filters` string from a combo-collection rule set.
 * Each facet may carry `.or` / `.and` value arrays which become
 * `(fooSlug:a OR fooSlug:b)` / `(fooSlug:a AND fooSlug:b)` groups (type values stay `type:x`).
 * Shared between fetchComboQuery and the InstantSearch-based collections page.
 * @param {Object} comboRules
 * @return {string}
 */
export function comboRulesToFilters(comboRules) {
  let filters = '';

  Object.keys(comboRules || {}).forEach((facet) => {
    if (comboRules[facet] && comboRules[facet].or && comboRules[facet].or.length) {
      filters += `${filters.length ? ' AND ' : ''}(${comboRules[facet].or.map(t => (facet === 'type' ? `type:${t}` : `${facet}Slug:${encodeSlug(t)}`)).join(' OR ')})`;
    }
    if (comboRules[facet] && comboRules[facet].and && comboRules[facet].and.length) {
      filters += `${filters.length ? ' AND ' : ''}(${comboRules[facet].and.map(t => (facet === 'type' ? `type:${t}` : `${facet}Slug:${encodeSlug(t)}`)).join(' AND ')})`;
    }
  });

  return filters;
}

export async function fetchComboQuery(comboRules, sort, page, facets) {
  const searchIndex = sort ? `${indexAlgoliaName}_${sort}` : topSellerIndex();

  const index = await getIndex(searchIndex);

  let filters = comboRulesToFilters(comboRules);

  if (sort === 'price_asc') filters += ' AND presale != 1';

  return index.search('', {
    hitsPerPage: 36,
    page,
    facets,
    filters,
    analyticsTags: ['frontend.fetchComboQuery'],
  });
}

export async function fetchHallOfFame({ bestOfYear, hitsPerPage }) {
  const index = await getIndex(`${indexAlgoliaName}_metacritic`);

  const { locale } = window;
  const attributesToRetrieve = globalAttributesToRetrieve.concat(['sdesc', `sdescs.${locale}`]);

  const startDate = (new Date(`${bestOfYear}-01-01T00:00:00.000Z`).getTime() / 1000);
  const endDate = (new Date(`${bestOfYear + 1}-01-01T00:00:00.000Z`).getTime() / 1000);

  return index.search('', {
    attributesToRetrieve,
    hitsPerPage,
    filters: `release_date > ${startDate} AND release_date < ${endDate} AND metacritic_score > 80 AND presale != 1 AND (type:game OR type:dlc) AND isEdition:false`,
    analyticsTags: ['frontend.fetchHallOfFame'],
  }).then(results => results.hits);
}

export async function fetchStandardUpsellDiscounts() {
  const indexTopSellers = await getIndex(topSellerIndex());
  return indexTopSellers.search('', {
    hitsPerPage: 15,
    filters: 'upsell = 1',
    analyticsTags: ['frontend.fetchStandardUpsellDiscounts'],
  }).then(results => results.hits);
}

/**
 * Fetches non-mystery upsell fallback array
 * @return {Object}             algolia results
 */
export async function fetchStandardUpsellsFallback(purchaseHistoryNames) {
  const index = await getIndex(topSellerIndex());

  const priceObject = {
    CAD: 300,
    USD: 300,
    EUR: 300,
    GBP: 300,
    AUD: 300,
    RUB: 3338,
    JPY: 5500,
  };

  let filters = `type:game AND discount_percent > 50 AND metacritic_score > 75 AND price.${currencyCode} < ${priceObject[currencyCode] / 100}`;

  if (!isEmpty(purchaseHistoryNames)) {
    filters = `${createNamesToExcludeFilter(purchaseHistoryNames)} AND type:game AND discount_percent > 50 AND metacritic_score > 75 AND price.${currencyCode} < ${priceObject[currencyCode] / 100}`;
  }

  return index.search('', {
    filters,
    analyticsTags: ['frontend.fetchStandardUpsellsFallback'],
  }).then(results => results.hits);
}

export async function fetchTopSellingPickAndMixes() {
  const indexBundles = await getIndex(topSellerIndex());

  return indexBundles.search('', {
    filters: 'type:pick-and-mix',
    hitsPerPage: 100,
    analyticsTags: ['frontend.fetchTopSellingPickAndMixes'],
  }).then(results => results.hits);
}

export async function fetchDlcBySlug(slug) {
  const index = await getIndex(topSellerIndex());
  return index.search('', {
    hitsPerPage: 100,
    filters: `(parent_slug:"${slug}") AND type:dlc`,
    analyticsTags: ['frontend.fetchDlcBySlug'],
  }).then(result => result.hits);
}


export async function fetchBundleWithMysterySection(hitsPerPage = 15) {
  const index = await getIndex(topSellerIndex());

  return index.search('', {
    filters: 'display_type:software-bundle OR display_type:bundle OR display_type:book-bundle OR display_type:elearning-bundle OR display_type:elearning OR display_type:comic-bundle OR display_type:audio-bundle OR display_type:video-bundle AND system_discount = 0',
    hitsPerPage,
    analyticsTags: ['frontend.fetchBundleWithMysterySection'],
  }).then(results => results.hits);
}


export function fetchMegaMenu() {
  return dispatch => apiGet(dispatch, apiAlgoliaSplitTest('/algolia/megamenu'), true)
    .then(({ response, data }) => {
      if (response && data) {
        return data;
      }
      return false;
    });
}
